import { apiUrl, authHeaders, credentialsMode } from '../api/origin.ts';
import {
  admit,
  enqueue,
  isNoise,
  readQueue,
  signatureOf,
  withoutIds,
  type ClientErrorKind,
  type ClientErrorReport,
} from './errorLog.ts';

/**
 * The device's own record of what it could not reach.
 *
 * errorLog.ts holds the rules; this holds the storage, the network and the
 * window. Two things about it are load-bearing:
 *
 * It does not go through `api/client.ts`. Reporting is the one request whose
 * failure must not be reported — a `request()` that filed a note on its own
 * failure would file a note on that note — so this posts with a bare `fetch`
 * and swallows everything. It is also the one request that has to work when
 * that module's assumptions do not. What it does take from `api/origin.ts` is
 * where to post and how to prove who it is, which is not an assumption but the
 * shape of the build; that module imports nothing, so there is no cycle.
 *
 * And the queue lives in localStorage rather than in memory, because the case
 * it exists for is the one where the tab is closed and reopened before the
 * network comes back. A report kept only in a variable would describe outages
 * that ended politely and none of the ones that did not.
 */

const STORAGE_KEY = 'chat:client-errors';

/** Must not exceed MAX_BATCH in server/src/client-errors.ts, which trims. */
const BATCH_LIMIT = 250;

/**
 * A report is never sent on the spot. The failure that produced it usually
 * means the network is down, so the immediate attempt is the one guaranteed to
 * fail — and errors arrive in bursts, so waiting turns five requests into one.
 */
const FLUSH_DELAY_MS = 3_000;

/** How often a queue that has something in it tries again on its own. */
const RETRY_MS = 60_000;

let queue: ClientErrorReport[] = [];
/** Last time each kind of failure was recorded; see `admit`. */
const seen = new Map<string, number>();
let flushTimer: number | null = null;
let flushing = false;
let installed = false;

/**
 * Storage may be unavailable — Safari's private mode throws on write, and a
 * quota that is full throws on a queue this small only when something else has
 * filled it. Diagnostics are the wrong thing to break a session over, so both
 * directions fail silently and the queue simply stops surviving reloads.
 */
function load(): void {
  try {
    queue = readQueue(localStorage.getItem(STORAGE_KEY));
  } catch {
    queue = [];
  }
}

function save(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(queue));
  } catch {
    // Nothing to do about it, and nothing worth telling anyone.
  }
}

function mintId(): string {
  // randomUUID needs a secure context, which an app served over plain HTTP on
  // a LAN address is not. The fallback only has to be unique enough that two
  // reports from one device do not collide.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface ClientErrorInput {
  kind: ClientErrorKind;
  /** The endpoint, socket or step — as it really was, ids included. */
  detail: string;
  message: string;
  /** 0 when nothing answered; omit when there was no request to answer. */
  status?: number | null;
}

/**
 * Where a rejected promise came from, for the `detail` column.
 *
 * Every unhandled rejection used to file the bare word `promise`, which says
 * only that one happened. Three of them turned out to be a camera flip, and the
 * only reason that was knowable is that a call journal happened to be sitting
 * beside them with the button presses in it — an accident, not a method. One
 * frame of the stack is a bundle path and two numbers, minified but not
 * anonymous: it names the module, which is the whole distance between «где-то
 * в приложении» and a file to open.
 *
 * Know what this does *not* reach, or it will be trusted for more than it is
 * worth. A `DOMException` raised by a web API carries no `stack` at all —
 * checked in Chrome against `getUserMedia`, which is exactly the camera failure
 * above, so that one still files the bare word. What does carry a stack is
 * everything this codebase throws itself: `new Error('a camera flip is already
 * in flight')` and its neighbours come back as
 * `promise · at flipCamera (…)`. So this narrows the rejections we caused, and
 * leaves the ones the platform handed us where they were.
 *
 * The first frame is skipped where a stack has one: V8 repeats the message
 * there, and the line under it is the code. Falls back to the old word, since a
 * report with no origin is still worth more than no report.
 */
function originOf(reason: unknown): string {
  if (!(reason instanceof Error) || typeof reason.stack !== 'string') return 'promise';
  const frame = reason.stack
    .split(/\r?\n/)
    .slice(1)
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  // The throttle keys on this, so an unbounded string here would be a signature
  // per stack; the server truncates at 200 in any case.
  return frame === undefined ? 'promise' : `promise · ${frame.slice(0, 120)}`;
}

/**
 * Files one note. Never throws, never awaits, and is safe to call from a catch
 * block anywhere — including one that is handling the failure of the very
 * thing this is trying to reach.
 */
export function reportClientError(input: ClientErrorInput): void {
  if (isNoise(input.message)) return;

  const now = Date.now();
  const report: ClientErrorReport = {
    id: mintId(),
    kind: input.kind,
    detail: input.detail,
    message: input.message.slice(0, 500),
    status: input.status ?? null,
    // Worth capturing at the moment it happened rather than at the moment it is
    // sent, which by definition is a moment when there is a network.
    offline: typeof navigator !== 'undefined' && navigator.onLine === false,
    at: now,
  };

  if (!admit(seen, signatureOf(report), now)) return;

  queue = enqueue(queue, report);
  save();
  scheduleFlush();
}

function scheduleFlush(delay = FLUSH_DELAY_MS): void {
  if (flushTimer !== null || queue.length === 0) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushClientErrors();
  }, delay);
}

/**
 * Hands over as much of the queue as one request may carry.
 *
 * Only what was actually sent is dropped, and only on a 2xx — a 401 leaves
 * everything queued, which is what carries an error from the login screen into
 * the session that follows it. Anything else is left alone too: a report that
 * could not be filed is still true.
 */
export async function flushClientErrors(): Promise<void> {
  if (flushing || queue.length === 0) return;
  flushing = true;

  const batch = queue.slice(0, BATCH_LIMIT);
  try {
    const response = await fetch(apiUrl('/client-errors'), {
      method: 'POST',
      credentials: credentialsMode(),
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ errors: batch }),
      // Deliberately not `keepalive`, which would let this outlive the page:
      // Chrome gives keepalive requests a 64 KB budget between them, and a full
      // batch is several times that, so it would fail exactly when there is
      // most to send. Nothing is lost by letting an unloading page drop it —
      // the queue is in localStorage, and a resend lands on rows it already
      // made.
    });
    if (response.ok) {
      queue = withoutIds(
        queue,
        batch.map((report) => report.id),
      );
      save();
    }
  } catch {
    // Still offline. The timer below is what tries again.
  } finally {
    flushing = false;
  }

  // More than one batch's worth waiting, or a fresh report filed mid-flight.
  if (queue.length > 0) scheduleFlush(RETRY_MS);
}

/**
 * Starts the reporter: reads what the last session left, listens for the two
 * things that produce a report on their own, and keeps a slow retry going.
 *
 * Called once, from main.tsx, before anything is rendered — an error thrown on
 * the way up is exactly the kind this is meant to catch.
 */
export function installClientErrorReporting(): void {
  if (installed) return;
  installed = true;

  load();

  window.addEventListener('error', (event) => {
    // A failed image or stylesheet fires this too, with no message and no
    // error object. Those are a broken thumbnail rather than a broken app, and
    // there are enough of them to bury everything else.
    if (!event.message && !event.error) return;
    reportClientError({
      kind: 'crash',
      detail: event.filename ? `${event.filename}:${event.lineno}` : 'window',
      message: event.message || String(event.error),
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason: unknown = event.reason;
    reportClientError({
      kind: 'crash',
      detail: originOf(reason),
      message: reason instanceof Error ? `${reason.name}: ${reason.message}` : String(reason),
    });
  });

  // The browser saying the network is back is the best moment there is to try
  // again; everything else here is a fallback for the many cases it does not fire.
  window.addEventListener('online', () => void flushClientErrors());

  window.setInterval(() => void flushClientErrors(), RETRY_MS);
  scheduleFlush(FLUSH_DELAY_MS);
}
